"use client";

import { LogOut, Wallet, User, X, Package, ChevronRight, Info, BookOpen, HelpCircle, FileText, Shield } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { usePrivyWallet } from "@/hooks/usePrivyWallet";
import { WalletConnectionOptions } from "@/components/WalletConnectionOptions";
import { WalletInfoDisplay } from "@/components/UI/wallet-info-display";

interface MobileMenuLoggedInProps {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
}

export const MobileMenuLoggedIn = ({
	isOpen,
	setIsOpen,
}: MobileMenuLoggedInProps) => {
	const router = useRouter();
	const { user, isConnected, logout: disconnectWallet } = usePrivyWallet();
	const lastTapAtRef = useRef(0);
	const previousBodyStylesRef = useRef<{
		overflow: string;
		position: string;
		top: string;
		width: string;
		paddingRight: string;
	}>({
		overflow: "",
		position: "",
		top: "",
		width: "",
		paddingRight: "",
	});

	// Lock body scroll when menu is open
	useEffect(() => {
		if (isOpen) {
			previousBodyStylesRef.current = {
				overflow: document.body.style.overflow,
				position: document.body.style.position,
				top: document.body.style.top,
				width: document.body.style.width,
				paddingRight: document.body.style.paddingRight,
			};
			// Save current scroll position
			const scrollY = window.scrollY;
			
			// Prevent body scroll
			const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
			document.body.style.overflow = 'hidden';
			document.body.style.position = 'fixed';
			document.body.style.top = `-${scrollY}px`;
			document.body.style.width = '100%';
			document.body.style.paddingRight = scrollBarWidth > 0 ? `${scrollBarWidth}px` : "";
		} else {
			// Restore body scroll
			const scrollY = document.body.style.top;
			document.body.style.overflow = previousBodyStylesRef.current.overflow;
			document.body.style.position = previousBodyStylesRef.current.position;
			document.body.style.top = previousBodyStylesRef.current.top;
			document.body.style.width = previousBodyStylesRef.current.width;
			document.body.style.paddingRight = previousBodyStylesRef.current.paddingRight;
			
			// Restore scroll position
			if (scrollY) {
				window.scrollTo(0, parseInt(scrollY || '0') * -1);
			}
		}

		// Cleanup on unmount - always restore scroll
		return () => {
			document.body.style.overflow = previousBodyStylesRef.current.overflow;
			document.body.style.position = previousBodyStylesRef.current.position;
			document.body.style.top = previousBodyStylesRef.current.top;
			document.body.style.width = previousBodyStylesRef.current.width;
			document.body.style.paddingRight = previousBodyStylesRef.current.paddingRight;
		};
	}, [isOpen]);

	const handleLogout = async () => {
		try {
			// Close menu immediately
			setIsOpen(false);

			await disconnectWallet();
		} catch (error) {
			console.error("Logout error:", error);
		}
	};

	const handleMenuClick = (path: string) => {
		try {
			// Keep navigation client-side to preserve hydrated auth state.
			router.push(path);
			setIsOpen(false);
		} catch (error) {
			console.error("Menu navigation error:", error);
		}
	};

	const runInstantAction = (action: () => void) => {
		const now = Date.now();
		if (now - lastTapAtRef.current < 150) {
			return;
		}
		lastTapAtRef.current = now;
		action();
	};

	const menuItems = [
		{
			label: "Profile",
			icon: <User className="w-5 h-5" />,
			onClick: () => handleMenuClick("/profile"),
			iconBg: "bg-mgt-green",
		},
		{
			label: "Drop Stash",
			icon: <Package className="w-5 h-5" />,
			onClick: () => handleMenuClick("/drop-nft"),
			iconBg: "bg-mgt-green",
		},
		{
			label: "Wallet",
			icon: <Wallet className="w-5 h-5" />,
			onClick: () => handleMenuClick("/wallet"),
			iconBg: "bg-mgt-green",
		},
		{
			label: "Logout",
			icon: <LogOut className="w-5 h-5" />,
			onClick: handleLogout,
			iconBg: "bg-gray-600",
		},
	];

	const aboutMenuItems = [
		{
			label: "About MyGeoTokens",
			icon: <Info className="w-5 h-5" />,
			onClick: () => handleMenuClick("/about"),
			iconBg: "bg-blue-500",
		},
		{
			label: "Start Guide",
			icon: <BookOpen className="w-5 h-5" />,
			onClick: () => handleMenuClick("/guide"),
			iconBg: "bg-purple-500",
		},
		{
			label: "FAQs",
			icon: <HelpCircle className="w-5 h-5" />,
			onClick: () => handleMenuClick("/faqs"),
			iconBg: "bg-orange-500",
		},
		{
			label: "Terms & Conditions",
			icon: <FileText className="w-5 h-5" />,
			onClick: () => handleMenuClick("/terms"),
			iconBg: "bg-indigo-500",
		},
		{
			label: "Privacy Policy",
			icon: <Shield className="w-5 h-5" />,
			onClick: () => handleMenuClick("/privacy"),
			iconBg: "bg-pink-500",
		},
	];

	// Only render on client side and use portal to render at document body level
	if (typeof window === 'undefined') return null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] lg:hidden pointer-events-none">
			{/* Enhanced Backdrop with blur */}
			<div
				className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 pointer-events-auto"
				onClick={() => setIsOpen(false)}
				style={{ opacity: isOpen ? 1 : 0 }}
			/>

			{/* Premium Menu Panel */}
			<div 
				className={`fixed top-0 right-0 h-[100dvh] w-full max-w-[340px] sm:max-w-[360px] bg-gradient-to-br from-brown via-mgt-black to-mgt-black border-l border-mgt-green/20 shadow-2xl transform transition-all duration-500 ease-out overflow-hidden ${
					isOpen ? "translate-x-0" : "translate-x-full"
				} pointer-events-auto`}
				style={{
					// no token: branded glow depth effect has no semantic token equivalent
					boxShadow: '0 0 50px rgba(70, 184, 118, 0.1), inset 0 0 100px rgba(0, 0, 0, 0.5)',
					paddingTop: "max(env(safe-area-inset-top), 0px)",
					paddingBottom: "max(env(safe-area-inset-bottom), 0px)"
				}}
				onClick={(e) => e.stopPropagation()}
			>
			<div className="flex flex-col h-full overflow-hidden">
				{/* Decorative gradient overlay */}
				<div className="absolute top-0 left-0 right-0 h-48 bg-gradient-to-b from-mgt-green/5 to-transparent pointer-events-none z-0" />
					
				{/* Header with logo and close button */}
				<div className="flex-shrink-0 relative z-10 backdrop-blur-xl bg-white/5 border-b border-white/10 p-4 flex items-center justify-between">
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 bg-mgt-green rounded-xl flex items-center justify-center">
							{/* eslint-disable-next-line @next/next/no-img-element */}
							<img 
								src="/icons/logo.gif" 
								alt="MyGeoTokens" 
								className="w-8 h-8 object-contain"
							/>
							</div>
							<div>
								<p className="text-white font-semibold text-base">MyGeoTokens</p>
								<p className="text-mgt-green text-xs">Location-Based NFTs</p>
							</div>
						</div>
						<button
							type="button"
							onClick={() => runInstantAction(() => setIsOpen(false))}
							className="text-gray-400 hover:text-white hover:bg-white/10 rounded-xl p-2 transition-all duration-300 hover:rotate-90"
						>
							<X className="w-5 h-5" />
						</button>
					</div>

				{/* Wallet Connection Status */}
				{!isConnected && (
					<div className="flex-shrink-0 px-4 pt-4 pb-4">
							<div className="bg-brown border border-off-white/40 rounded-2xl p-4">
								<WalletConnectionOptions />
							</div>
						</div>
					)}

				{/* Wallet Info Card */}
				{isConnected && (
					<div className="flex-shrink-0 px-4 pt-4 pb-3">
							<div className="bg-brown border border-mgt-green/30 rounded-2xl p-5 shadow-lg">
								<WalletInfoDisplay
									showBalance={true}
									showNFTCounts={true}
									showFullAddress={false}
									compact={false}
									className="text-sm"
								/>
							</div>
						</div>
					)}

			{/* Menu Items with enhanced styling */}
			<div 
				className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y" 
				style={{ 
					WebkitOverflowScrolling: 'touch',
					touchAction: 'pan-y'
				}}
			>
				<div className="px-4 py-4 space-y-4 pb-6">
							{/* Main Menu Items */}
							<div className="space-y-2">
								<h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider px-2 mb-2">
									Main Menu
								</h3>
								{menuItems.map((item, index) => (
									<button
										key={index}
										type="button"
										onClick={() => runInstantAction(item.onClick)}
										className="group w-full relative overflow-hidden touch-manipulation"
										style={{
											animationDelay: `${index * 50}ms`,
											animation: isOpen ? 'slideInRight 0.3s ease-out forwards' : 'none'
										}}
									>
										{/* Content */}
										<div className="relative flex items-center gap-4 px-5 py-4 rounded-2xl bg-brown border border-off-white/40 group-hover:border-mgt-green/50 transition-all duration-300 group-hover:bg-brown/90">
											{/* Icon with solid background */}
											<div className={`${item.iconBg} p-3 rounded-xl text-black transition-all duration-300`}>
												{item.icon}
											</div>
											
											{/* Label */}
											<span className="flex-1 text-white font-medium text-base transition-colors text-left">
												{item.label}
											</span>
											
											{/* Arrow indicator */}
											<ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-mgt-green group-hover:translate-x-1 transition-all duration-300" />
										</div>
									</button>
								))}
							</div>

							{/* About Section */}
							<div className="space-y-2">
								<h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider px-2 mb-2 mt-4">
									About & Support
								</h3>
								{aboutMenuItems.map((item, index) => (
									<button
										key={index}
										type="button"
										onClick={() => runInstantAction(item.onClick)}
										className="group w-full relative overflow-hidden touch-manipulation"
										style={{
											animationDelay: `${(menuItems.length + index) * 50}ms`,
											animation: isOpen ? 'slideInRight 0.3s ease-out forwards' : 'none'
										}}
									>
										{/* Content */}
										<div className="relative flex items-center gap-4 px-5 py-4 rounded-2xl bg-brown border border-off-white/40 group-hover:border-mgt-green/50 transition-all duration-300 group-hover:bg-brown/90">
											{/* Icon with solid background */}
											<div className={`${item.iconBg} p-3 rounded-xl text-white transition-all duration-300`}>
												{item.icon}
											</div>
											
											{/* Label */}
											<span className="flex-1 text-white font-medium text-base transition-colors text-left">
												{item.label}
											</span>
											
											{/* Arrow indicator */}
											<ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-mgt-green group-hover:translate-x-1 transition-all duration-300" />
										</div>
									</button>
								))}
							</div>
						</div>
					</div>

				{/* Footer decoration */}
				<div className="flex-shrink-0 relative px-6 py-4 backdrop-blur-xl bg-white/5 border-t border-white/10">
					<p className="text-center text-gray-500 text-xs">
						MyGeoTokens • Secure & Connected
					</p>
				</div>
				</div>
			</div>

			<style jsx>{`
				@keyframes slideInRight {
					from {
						opacity: 0;
						transform: translateX(20px);
					}
					to {
						opacity: 1;
						transform: translateX(0);
					}
				}
			`}</style>
		</div>,
		document.body
	);
};
